import * as angular from 'angular';
import type { IHttpInterceptor } from 'angular';
import uiRouter, { TransitionService } from '@uirouter/angularjs';

import { AuthBehaviorProviderService } from './mx-auth-behavior-provider-service';
import { authorizationInterceptor } from './authorizationInterceptor';
import { AuthenticationService } from './mx-authentication-service';
import { clearFailedAttempts } from '../Login/utils.login';
import type { HookMatchCriteria, TransitionHookFn } from '@uirouter/core/lib/transition/interface';
import type { IHttpInterceptorsRegistry } from '../App/services/http-interceptors-registry';

export const authModule = angular
	.module('mx.sb.auth', [uiRouter])
	.service('mx.shell.AuthBehaviorProviderService', AuthBehaviorProviderService)
	.factory('mx.shell.AuthorizationInterceptor', authorizationInterceptor)
	.service('mx.shell.AuthService', AuthenticationService)
	.config(configAuthModule)
	.run(runAuthModule);

let tokenListenerCleanup: (() => void) | null = null;

configAuthModule.$inject = ['$transitionsProvider'];
function configAuthModule($transitions: TransitionService) {
	const requiresAuthCriteria: HookMatchCriteria = {
		to: (state) => !state?.data || (state.data && !state.data.isAnonymous),
	};
	const redirectToLogin: TransitionHookFn = async (transition) => {
		const authService = transition.injector().get('mx.shell.AuthService') as AuthenticationService;

		if (!(await authService.isAuthenticated())) {
			try {
				const session = await authService.login(parseDirectTokenFromUrl());

				if (window.location.href.indexOf('#') > -1 && typeof window.history.replaceState === 'function' && session) {
					// Login just succeeded and the URL still carries a hash fragment —
					// typically the OAuth2 implicit grant return `#access_token=...`,
					// but this covers any hash left over from the login round-trip.
					// We need to:
					//   1) strip the fragment from the address bar (don't leak credentials, clean URL),
					//   2) continue the transition to the originally requested state.
					// Returning `false` from an onBefore hook aborts the transition and UI-Router does NOT auto-retry —
					// the app would stay on the loading indicator forever.
					// Instead: strip the hash synchronously via replaceState, then return a TargetState
					// so UI-Router redirects to the same destination using the cleaned URL.
					history.replaceState({}, '', window.location.href.substr(0, window.location.href.indexOf('#')));
					clearFailedAttempts();
					const $state = transition.injector().get('$state');
					return $state.target(transition.to(), transition.params(), { location: 'replace' });
				}

				clearFailedAttempts();
			} catch (e) {
				authService.logout();
				return false;
			}
		}
		return true;
	};
	$transitions.onBefore(requiresAuthCriteria, redirectToLogin, { priority: 100 });
}

runAuthModule.$inject = ['mx.app.HttpInterceptorsRegistry', 'mx.shell.AuthorizationInterceptor', 'mx.shell.AuthService'];
function runAuthModule(httpInterceptorsRegistry: IHttpInterceptorsRegistry, authorizationInterceptor: IHttpInterceptor, authenticationService: AuthenticationService) {
	httpInterceptorsRegistry.register(authorizationInterceptor);

	const orgUnit = parseOrgUnitFromUrl();
	if (orgUnit) {
		authenticationService.initOrgUnit(orgUnit);
	}

	if (tokenListenerCleanup) {
		tokenListenerCleanup();
		tokenListenerCleanup = null;
	}

	tokenListenerCleanup = authenticationService.setupTokenRemovalListener();
}

function parseDirectTokenFromUrl() {
	try {
		const regex = /[?&]direct-token=([^&#]*)/;
		const match = regex.exec(window.location.search);
		return match ? decodeURIComponent(match[1]) : undefined;
	} catch (e) {
		return;
	}
}

function parseOrgUnitFromUrl() {
	try {
		const regex = /[?&]ou=([^&#]*)/;
		const match = regex.exec(window.location.search);
		return match ? decodeURIComponent(match[1]) : undefined;
	} catch (e) {
		return;
	}
}
